export const buildWsBaseUrlFromHttp = (httpBaseUrl) => {
  try {
    const parsed = new URL(httpBaseUrl);
    const protocol = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
    return `${protocol}//${parsed.host}`;
  } catch (error) {
    if (typeof httpBaseUrl === 'string') {
      return httpBaseUrl
        .replace(/^https:\/\//i, 'wss://')
        .replace(/^http:\/\//i, 'ws://')
        .replace(/\/+$/, '');
    }
    return '';
  }
};

export class WhatsappWsClient {
  constructor({
    wsBaseUrl,
    getFirebaseToken,
    onEvent,
    onError = null,
    onOpen = null,
    onClose = null,
    onReconnectFailed = null,
    maxReconnectAttempts = 5,
  }) {
    this.wsBaseUrl = String(wsBaseUrl || '').replace(/\/+$/, '');
    this.getFirebaseToken = getFirebaseToken;
    this.onEvent = onEvent;
    this.onError = onError;
    this.onOpen = onOpen;
    this.onClose = onClose;
    this.onReconnectFailed = onReconnectFailed;

    this.socket = null;
    this.reconnectAttempts = 0;
    this.reconnectTimer = null;
    this.manualClose = false;
    this.maxReconnectAttempts = Number.isFinite(maxReconnectAttempts)
      ? Math.max(1, Math.trunc(maxReconnectAttempts))
      : 5;
  }

  async connect() {
    this.manualClose = false;

    if (!this.wsBaseUrl) {
      throw new Error('Missing wsBaseUrl');
    }

    const token = await this.getFirebaseToken(this.reconnectAttempts > 0);
    const url = `${this.wsBaseUrl}/ws/subscribe?token=${encodeURIComponent(token)}`;

    this.socket = new WebSocket(url);

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      if (typeof this.onOpen === 'function') {
        this.onOpen();
      }
    };

    this.socket.onmessage = (message) => {
      try {
        const parsed = JSON.parse(message.data);
        if (typeof this.onEvent === 'function') {
          this.onEvent(parsed);
        }
      } catch (error) {
        if (typeof this.onError === 'function') {
          this.onError(error);
        }
      }
    };

    this.socket.onerror = (error) => {
      if (typeof this.onError === 'function') {
        this.onError(error);
      }
    };

    this.socket.onclose = () => {
      if (typeof this.onClose === 'function') {
        this.onClose({ manualClose: this.manualClose });
      }
      if (!this.manualClose) {
        this.scheduleReconnect();
      }
    };
  }

  close() {
    this.manualClose = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      if (typeof this.onReconnectFailed === 'function') {
        this.onReconnectFailed({ attempts: this.reconnectAttempts });
      }
      return;
    }

    this.reconnectAttempts += 1;
    const backoffMs = Math.min(1000 * 2 ** this.reconnectAttempts, 30000);

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(() => {
      this.connect().catch((error) => {
        if (typeof this.onError === 'function') {
          this.onError(error);
        }
      });
    }, backoffMs);
  }
}
